/**
 * SAM-553 — pure validation for the refund dialog, split out of the component so it can be unit
 * tested without rendering React. Irreversible action, so this is deliberately strict: a reason is
 * always required, and a partial amount can never exceed what was actually captured.
 */

export interface RefundValidationResult {
  valid: boolean;
  /** Parsed amount, or undefined for "refund the full captured amount" (empty input). */
  amount?: number;
  error?: string;
}

/**
 * @param amountInput Raw text from the amount field. Empty/blank means "full refund" and is valid.
 * @param capturedAmount The amount actually captured on this booking (booking.amount) — the ceiling
 *   a partial refund may not exceed. `null`/`undefined` means unknown, so any partial amount is
 *   rejected rather than allowed through on an assumption.
 * @param reason Free-text reason; required and must not be only whitespace.
 */
export function validateRefundInput(
  amountInput: string,
  capturedAmount: number | null | undefined,
  reason: string,
): RefundValidationResult {
  if (!reason || !reason.trim()) {
    return { valid: false, error: 'A reason is required.' };
  }

  const trimmed = amountInput.trim();
  if (trimmed === '') {
    // Full refund of the captured amount — the server computes the figure; nothing to send.
    return { valid: true, amount: undefined };
  }

  const amount = Number(trimmed);
  if (!Number.isFinite(amount) || Number.isNaN(amount)) {
    return { valid: false, error: 'Enter a valid amount.' };
  }
  if (amount <= 0) {
    return { valid: false, error: 'Amount must be greater than zero.' };
  }
  if (capturedAmount == null) {
    return { valid: false, error: 'Captured amount is unknown; cannot validate a partial refund.' };
  }
  if (amount > capturedAmount) {
    return { valid: false, error: `Amount cannot exceed the captured total of ${capturedAmount.toFixed(2)}.` };
  }
  return { valid: true, amount };
}
